import { useCallback, useEffect, useRef } from 'react';

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { CSS2DObject, CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

import { DXFData } from '../new-project/dxf/types';
import { CadGrid, createCadGrid } from './cadGrid';
import { buildShapeWithHoles, circlePoints, pointInPolygon, screenToWorld, snapToGrid } from './cadUtils';
import { CadPolygon, CadTool, CadViewMode } from './types';

interface CadCanvasProps {
  dxfData: DXFData | null;
  imageUrl: string | null;
  imageScale: number;
  polygons: CadPolygon[];
  activePolygonIndex: number | null;
  drawingPoints: [number, number][];
  tool: CadTool;
  viewMode: CadViewMode;
  snapEnabled: boolean;
  gridSize: number;
  onPolygonComplete: (points: [number, number][], holes?: [number, number][][]) => void;
  onDrawingPointsChange: (points: [number, number][]) => void;
  onCoordChange: (coord: { x: number; y: number }) => void;
  highlightedLayer: string | null;
  onPolygonSelect: (index: number | null) => void;
  /** Polygons ticked for a change to all of them: shown on the plan as in the list. */
  selectedPolygonIds?: Set<string>;
  /** Shift/⌘/Ctrl + click on a polygon ticks it (or unticks it). */
  onPolygonToggleSelected?: (id: string) => void;
  onUpdatePolygonPoints: (id: string, points: [number, number][]) => void;
  /** Silent update during drag — does NOT push to undo stack. */
  onUpdatePolygonPointsSilent?: (id: string, points: [number, number][]) => void;
  /** Manually push an undo snapshot after drag completes. */
  onPushUndoSnapshot?: (polygonId: string, oldPoints: [number, number][]) => void;
  captureRef: React.MutableRefObject<(() => string) | null>;
  onZoomChange?: (frustumWidth: number) => void;
  metersPerUnit: number;
  measurementPoints?: [number, number][];
  onAddMeasurementPoint?: (point: [number, number]) => void;
  onClearMeasurement?: () => void;
  onImageScaleChange?: (scale: number) => void;
  /** Scale calibration */
  scalePoints?: [number, number][];
  scaleSet?: boolean;
  onAddScalePoint?: (point: [number, number]) => void;
  onClearScalePoints?: () => void;
}

// ── Palette ──

const POLY_FILL = 0x888888;
const POLY_FILL_OPACITY = 0.25;
const POLY_STROKE = 0xdd2222;
const POLY_ACTIVE_FILL = 0xff2222;
const POLY_ACTIVE_FILL_OPACITY = 0.22;
const POLY_ACTIVE_STROKE = 0xff5555;
// Ticked polygons, lime like the list's checkboxes.
const POLY_SELECTED_STROKE = 0xb3ec49;
const POLY_SELECTED_FILL = 0xb3ec49;
const POLY_SELECTED_FILL_OPACITY = 0.28;
const NO_SELECTION = new Set<string>();

/** Outline and fill of a polygon on the plan: ticked ones lime, the active one red, the rest grey. */
function polygonStyle(isActive: boolean, isSelected: boolean) {
  if (isSelected) {
    return {
      stroke: POLY_SELECTED_STROKE,
      fill: POLY_SELECTED_FILL,
      fillOpacity: POLY_SELECTED_FILL_OPACITY,
      width: 3,
    };
  }
  if (isActive) {
    return { stroke: POLY_ACTIVE_STROKE, fill: POLY_ACTIVE_FILL, fillOpacity: POLY_ACTIVE_FILL_OPACITY, width: 3 };
  }
  return { stroke: POLY_STROKE, fill: POLY_FILL, fillOpacity: POLY_FILL_OPACITY, width: 2 };
}

/** In-progress draw preview fill — slightly denser so shapes read clearly over imagery. */
const PREVIEW_FILL_OPACITY = 0.22;
const PREVIEW_SUBTRACT_FILL_OPACITY = 0.28;

// ── Helpers ──────────────────────────────────────────────────────────────────

/** The imported drawing's lines (one object per layer) in the background group. */
const isDrawingLines = (child: THREE.Object3D) => child instanceof THREE.LineSegments;
/** The plan image in the background group. */
const isImageMesh = (child: THREE.Object3D) => (child as THREE.Mesh).isMesh === true;

function clearGroup(g: THREE.Group, only: (child: THREE.Object3D) => boolean = () => true) {
  // Remove the (matching) children and dispose their resources
  const children = g.children.filter(only);
  for (const c of children) {
    g.remove(c);
    c.traverse((o: THREE.Object3D) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose();
      if (m.material) {
        if (Array.isArray(m.material)) {
          for (const mat of m.material) {
            (mat as THREE.Material & { map?: THREE.Texture }).map?.dispose();
            mat.dispose();
          }
        } else {
          const mat = m.material as THREE.Material & { map?: THREE.Texture };
          mat.map?.dispose();
          mat.dispose();
        }
      }
    });
  }
}

/** Create a thick Line2 from an array of [x,y] points at a given Z depth. */
function makeLine2(
  pts: [number, number][],
  z: number,
  color: number,
  lineWidth: number,
  resolution: THREE.Vector2,
): Line2 {
  const positions: number[] = [];
  for (const [x, y] of pts) positions.push(x, y, z);
  const geo = new LineGeometry();
  geo.setPositions(positions);
  const mat = new LineMaterial({
    color,
    linewidth: lineWidth,
    resolution,
    worldUnits: false,
    depthTest: false,
    depthWrite: false,
    transparent: true,
    opacity: 1.0,
  });
  return new Line2(geo, mat);
}

/** Create a thick dashed Line2. */
function makeDashedLine2(
  pts: [number, number][],
  z: number,
  color: number,
  lineWidth: number,
  opacity: number,
  resolution: THREE.Vector2,
  dashSize = 2,
  gapSize = 1.5,
): Line2 {
  const positions: number[] = [];
  for (const [x, y] of pts) positions.push(x, y, z);
  const geo = new LineGeometry();
  geo.setPositions(positions);
  const mat = new LineMaterial({
    color,
    linewidth: lineWidth,
    resolution,
    worldUnits: false,
    dashed: true,
    dashSize,
    gapSize,
    transparent: true,
    opacity,
    depthTest: false,
    depthWrite: false,
  });
  const line = new Line2(geo, mat);
  line.computeLineDistances();
  return line;
}

/** Pixel radius for dots (constant screen-size). */
const DOT_SCREEN_PX = 2.5;
const DOT_HOVER_SCREEN_PX = 2.5; // same size on hover - only color changes

// ── Component ────────────────────────────────────────────────────────────────

export function CadCanvas({
  dxfData,
  imageUrl,
  imageScale,
  polygons,
  activePolygonIndex,
  drawingPoints,
  tool,
  viewMode,
  snapEnabled,
  gridSize,
  highlightedLayer,
  onPolygonComplete,
  onDrawingPointsChange,
  onCoordChange,
  onPolygonSelect,
  selectedPolygonIds,
  onPolygonToggleSelected,
  onUpdatePolygonPoints,
  onUpdatePolygonPointsSilent,
  onPushUndoSnapshot,
  captureRef,
  onZoomChange,
  metersPerUnit,
  measurementPoints = [],
  onAddMeasurementPoint,
  onClearMeasurement,
  onImageScaleChange,
  scalePoints = [],
  scaleSet = false,
  onAddScalePoint,
  onClearScalePoints,
}: CadCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Store mutable refs for event handlers to avoid stale closures
  const stateRef = useRef({
    tool,
    viewMode,
    drawingPoints,
    polygons,
    snapEnabled,
    gridSize,
    activePolygonIndex,
    imageScale,
    metersPerUnit,
    measurementPoints,
    scalePoints,
    scaleSet,
  });
  useEffect(() => {
    stateRef.current = {
      tool,
      viewMode,
      drawingPoints,
      polygons,
      snapEnabled,
      gridSize,
      activePolygonIndex,
      imageScale,
      metersPerUnit,
      measurementPoints,
      scalePoints,
      scaleSet,
    };
  });

  // Refs for Three.js objects — stable across renders
  const threeRef = useRef<{
    renderer: THREE.WebGLRenderer;
    css2dRenderer: CSS2DRenderer;
    scene: THREE.Scene;
    cam2d: THREE.OrthographicCamera;
    cam3d: THREE.PerspectiveCamera;
    controls3d: OrbitControls;
    bgGroup: THREE.Group;
    polyGroup: THREE.Group;
    previewGroup: THREE.Group;
    extGroup: THREE.Group;
    cadGrid: CadGrid;
    vertexOverlayGroup: THREE.Group;
    measureGroup: THREE.Group;
    scaleGroup: THREE.Group;
    bboxGroup: THREE.Group;
    edgeDistGroup: THREE.Group;
    handleGroup: THREE.Group;
    rulerGroup: THREE.Group;
    animId: number;
  } | null>(null);

  // Drag state (pan, orbit, click detection)
  const dragRef = useRef({
    type: null as string | null,
    sx: 0,
    sy: 0,
    camX: 0,
    camY: 0,
    moved: false,
  });

  // Circle tool state
  const circleRef = useRef({
    active: false,
    centerX: 0,
    centerY: 0,
  });

  // Vertex drag state
  const vertexDragRef = useRef<{
    active: boolean;
    polygonId: string;
    polygonIndex: number;
    vertexIndex: number;
  }>({ active: false, polygonId: '', polygonIndex: -1, vertexIndex: -1 });

  // Polygon drag state (long press to move entire polygon)
  const polygonDragRef = useRef<{
    active: boolean;
    polygonId: string;
    startWorld: [number, number];
    originalPoints: [number, number][];
  }>({ active: false, polygonId: '', startWorld: [0, 0], originalPoints: [] });
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Snapshot of points at drag start — used to push a single undo command on drag end
  const dragStartSnapshotRef = useRef<{ polygonId: string; points: [number, number][] } | null>(null);

  // Hovered vertex (for visual feedback)
  const hoveredVertexRef = useRef<{ polygonIndex: number; vertexIndex: number }>({
    polygonIndex: -1,
    vertexIndex: -1,
  });

  // Last known mouse world position — used to redraw preview on undo without mouse move
  const lastMouseWorldRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Canvas resolution for LineMaterial
  const resolutionRef = useRef(new THREE.Vector2(800, 600));

  // Image resize drag state
  const imageResizeRef = useRef<{
    active: boolean;
    startScale: number;
    /** World position of the grabbed corner at drag start */
    startX: number;
    startY: number;
  }>({ active: false, startScale: 0, startX: 0, startY: 0 });

  // Persistent image refs — mesh created once, scale updated separately
  const imageNaturalRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const imageMeshRef = useRef<THREE.Mesh | null>(null);

  // Callback refs for mutable functions
  const completeRef = useRef(onPolygonComplete);
  completeRef.current = onPolygonComplete;
  const drawingChangeRef = useRef(onDrawingPointsChange);
  drawingChangeRef.current = onDrawingPointsChange;
  const coordChangeRef = useRef(onCoordChange);
  coordChangeRef.current = onCoordChange;
  const selectRef = useRef(onPolygonSelect);
  selectRef.current = onPolygonSelect;
  const selectedIdsRef = useRef(selectedPolygonIds ?? NO_SELECTION);
  selectedIdsRef.current = selectedPolygonIds ?? NO_SELECTION;
  const toggleSelectedRef = useRef(onPolygonToggleSelected);
  toggleSelectedRef.current = onPolygonToggleSelected;
  const silentUpdateRef = useRef(onUpdatePolygonPointsSilent);
  silentUpdateRef.current = onUpdatePolygonPointsSilent;
  const pushUndoRef = useRef(onPushUndoSnapshot);
  pushUndoRef.current = onPushUndoSnapshot;
  const updatePointsRef = useRef(onUpdatePolygonPoints);
  updatePointsRef.current = onUpdatePolygonPoints;
  const zoomChangeRef = useRef(onZoomChange);
  zoomChangeRef.current = onZoomChange;
  const addMeasureRef = useRef(onAddMeasurementPoint);
  addMeasureRef.current = onAddMeasurementPoint;
  const clearMeasureRef = useRef(onClearMeasurement);
  clearMeasureRef.current = onClearMeasurement;
  const imageScaleChangeRef = useRef(onImageScaleChange);
  imageScaleChangeRef.current = onImageScaleChange;
  const addScalePointRef = useRef(onAddScalePoint);
  addScalePointRef.current = onAddScalePoint;
  const clearScalePointsRef = useRef(onClearScalePoints);
  clearScalePointsRef.current = onClearScalePoints;
  // Ref to call updateImageOverlays from pointer-move event handler (set after useCallback below)
  const updateImageOverlaysRef = useRef<
    ((t: NonNullable<typeof threeRef.current>, iw: number, ih: number) => void) | null
  >(null);

  // ── Redraw drawing preview when drawingPoints changes (e.g. undo) ────────
  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;
    clearGroup(t.previewGroup);

    const pts = drawingPoints;
    if (pts.length === 0) return;

    const s = stateRef.current;
    const isSubtract = s.tool === 'subtract' || s.tool === 'split';
    const lineColor = isSubtract ? 0xff6600 : 0xff2222;
    const fillColor = isSubtract ? 0xff4400 : 0xff0000;
    const fillOpacity = isSubtract ? PREVIEW_SUBTRACT_FILL_OPACITY : PREVIEW_FILL_OPACITY;
    const { x: mx, y: my } = lastMouseWorldRef.current;

    const allPts = [...pts.map(([px, py]) => new THREE.Vector3(px, py, 0.2)), new THREE.Vector3(mx, my, 0.2)];
    if (pts.length >= 3) allPts.push(new THREE.Vector3(pts[0][0], pts[0][1], 0.2));

    if (allPts.length >= 2) {
      const pts2d = allPts.map((v) => [v.x, v.y] as [number, number]);
      t.previewGroup.add(makeLine2(pts2d, 0.2, lineColor, isSubtract ? 4 : 2, resolutionRef.current));
    }

    if (pts.length >= 3) {
      const shPts = [...pts, [mx, my] as [number, number]];
      const sh = new THREE.Shape();
      sh.moveTo(shPts[0][0], shPts[0][1]);
      shPts.slice(1).forEach(([px, py]) => sh.lineTo(px, py));
      sh.closePath();
      t.previewGroup.add(
        new THREE.Mesh(
          new THREE.ShapeGeometry(sh),
          new THREE.MeshBasicMaterial({
            color: fillColor,
            transparent: true,
            opacity: fillOpacity,
            side: THREE.DoubleSide,
            depthWrite: false,
            depthTest: false,
          }),
        ),
      );
    }

    pts.forEach(([px, py]) => {
      const dot = new THREE.Mesh(
        new THREE.CircleGeometry(1, 16),
        new THREE.MeshBasicMaterial({ color: lineColor, depthWrite: false, depthTest: false, transparent: true }),
      );
      dot.position.set(px, py, 0.2);
      dot.userData.dotScreenPx = DOT_SCREEN_PX;
      t.previewGroup.add(dot);
    });
  }, [drawingPoints]);

  // ══════════════════════════════════════════════════════════════════════════
  //  INIT THREE.JS
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (!w || !h) return;
    const asp = w / h;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0d0d0d);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.domElement.style.display = 'block'; // prevent inline baseline gap
    el.appendChild(renderer.domElement);
    resolutionRef.current.set(w, h);

    // CSS2D Renderer for labels
    const css2dRenderer = new CSS2DRenderer();
    css2dRenderer.setSize(w, h);
    css2dRenderer.domElement.style.position = 'absolute';
    css2dRenderer.domElement.style.top = '0';
    css2dRenderer.domElement.style.left = '0';
    css2dRenderer.domElement.style.pointerEvents = 'none';
    el.appendChild(css2dRenderer.domElement);

    // Cameras
    const halfH = 400;
    const cam2d = new THREE.OrthographicCamera(-halfH * asp, halfH * asp, halfH, -halfH, -5000, 5000);
    cam2d.position.set(0, 0, 100);
    cam2d.up.set(0, 1, 0);
    cam2d.quaternion.identity();
    cam2d.updateProjectionMatrix();
    zoomChangeRef.current?.(cam2d.right - cam2d.left);

    const cam3d = new THREE.PerspectiveCamera(50, asp, 0.1, 50000);
    cam3d.position.set(300, 300, 300);
    cam3d.lookAt(0, 0, 0);

    // OrbitControls for 3D mode
    const controls3d = new OrbitControls(cam3d, renderer.domElement);
    controls3d.enableDamping = true;
    controls3d.dampingFactor = 0.1;
    controls3d.enabled = false; // disabled until 3D mode

    // Grid — same CustomGrid implementation as the workspace, used in both 2D and 3D views.
    const cadGrid = createCadGrid();
    scene.add(cadGrid.group);

    // Groups — renderOrder ensures everything draws above the background image
    const bgGroup = new THREE.Group();
    bgGroup.renderOrder = -5;
    const polyGroup = new THREE.Group();
    polyGroup.renderOrder = 1;
    const previewGroup = new THREE.Group();
    previewGroup.renderOrder = 2;
    const extGroup = new THREE.Group();
    const vertexOverlayGroup = new THREE.Group();
    vertexOverlayGroup.renderOrder = 3;
    const measureGroup = new THREE.Group();
    measureGroup.renderOrder = 4;
    const scaleGroup = new THREE.Group();
    scaleGroup.renderOrder = 5;
    const bboxGroup = new THREE.Group();
    bboxGroup.renderOrder = 3;
    const edgeDistGroup = new THREE.Group();
    edgeDistGroup.renderOrder = 4;
    const handleGroup = new THREE.Group();
    handleGroup.renderOrder = 5;
    const rulerGroup = new THREE.Group();
    scene.add(
      bgGroup,
      polyGroup,
      previewGroup,
      extGroup,
      vertexOverlayGroup,
      measureGroup,
      scaleGroup,
      bboxGroup,
      edgeDistGroup,
      handleGroup,
      rulerGroup,
    );

    // Lights (for 3D mode)
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const dl = new THREE.DirectionalLight(0xffffff, 0.8);
    dl.position.set(500, 500, 700);
    scene.add(dl);

    threeRef.current = {
      renderer,
      css2dRenderer,
      scene,
      cam2d,
      cam3d,
      controls3d,
      bgGroup,
      polyGroup,
      previewGroup,
      extGroup,
      cadGrid,
      vertexOverlayGroup,
      measureGroup,
      scaleGroup,
      bboxGroup,
      edgeDistGroup,
      handleGroup,
      rulerGroup,
      animId: 0,
    };

    // Animation loop
    const loop = () => {
      const t = threeRef.current;
      if (!t) return;
      t.animId = requestAnimationFrame(loop);
      const is3d = stateRef.current.viewMode === '3d';
      const activeCam = is3d ? cam3d : cam2d;
      if (is3d) {
        t.controls3d.update();
      }
      t.cadGrid.update(activeCam, is3d ? t.controls3d.target : undefined);

      // Scale all dot meshes to constant screen size
      if (stateRef.current.viewMode === '2d') {
        const frustW = cam2d.right - cam2d.left;
        const canvasW = el.clientWidth || 1;
        const worldPerPx = frustW / canvasW;
        const groups = [
          t.polyGroup,
          t.previewGroup,
          t.vertexOverlayGroup,
          t.measureGroup,
          t.scaleGroup,
          t.edgeDistGroup,
          t.handleGroup,
        ];
        for (const g of groups) {
          g.traverse((obj) => {
            if (obj.userData.dotScreenPx) {
              const r = worldPerPx * obj.userData.dotScreenPx;
              obj.scale.setScalar(r);
            }
          });
        }
      }

      renderer.render(scene, activeCam);
      css2dRenderer.render(scene, activeCam);
    };
    loop();

    // ── WHEEL ──
    /** Zooms the 2D view by `zf` (above 1 zooms out) keeping the point under clientX/Y in place. */
    const zoomAt = (clientX: number, clientY: number, zf: number) => {
      const rect = el.getBoundingClientRect();
      const mx = ((clientX - rect.left) / rect.width) * 2 - 1;
      const my = -((clientY - rect.top) / rect.height) * 2 + 1;
      const oW = cam2d.right - cam2d.left;
      const oH = cam2d.top - cam2d.bottom;
      const nH = oH * zf;
      if (nH < 0.5 || nH > 30000) return;

      const wxB = cam2d.position.x + (mx * oW) / 2;
      const wyB = cam2d.position.y + (my * oH) / 2;
      cam2d.left *= zf;
      cam2d.right *= zf;
      cam2d.top *= zf;
      cam2d.bottom *= zf;
      const nW = cam2d.right - cam2d.left;
      const wxA = cam2d.position.x + (mx * nW) / 2;
      const wyA = cam2d.position.y + (my * (cam2d.top - cam2d.bottom)) / 2;
      cam2d.position.x += wxB - wxA;
      cam2d.position.y += wyB - wyA;
      cam2d.updateProjectionMatrix();
      zoomChangeRef.current?.(cam2d.right - cam2d.left);
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (stateRef.current.viewMode === '3d') return; // OrbitControls handles 3D zoom
      zoomAt(e.clientX, e.clientY, e.deltaY > 0 ? 1.12 : 1 / 1.12);
    };
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });

    // ── POINTER DOWN ──
    const onDown = (e: PointerEvent) => {
      const d = dragRef.current;
      d.moved = false;
      const s = stateRef.current;

      if (s.viewMode === '3d') return; // OrbitControls handles 3D

      // Check for image resize handle hit (skip when scale is already set)
      if (e.button === 0 && !s.scaleSet && threeRef.current && threeRef.current.handleGroup.children.length > 0) {
        const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (wc) {
          const vw = cam2d.right - cam2d.left;
          const hitR = vw * 0.02;
          for (const child of threeRef.current.handleGroup.children) {
            if (child.userData.isResizeHandle) {
              const dx = wc[0] - child.position.x;
              const dy = wc[1] - child.position.y;
              if (Math.hypot(dx, dy) < hitR) {
                const hx = child.position.x;
                const hy = child.position.y;
                // Bottom-left corner (0,0) is the fixed origin — skip it
                if (hx === 0 && hy === 0) return;
                imageResizeRef.current = {
                  active: true,
                  startScale: s.imageScale,
                  startX: hx,
                  startY: hy,
                };
                d.type = 'image-resize';
                d.sx = e.clientX;
                d.sy = e.clientY;
                renderer.domElement.style.cursor = 'nwse-resize';
                return;
              }
            }
          }
        }
      }

      // Middle/right click = pan, or left click in pan mode
      if (e.button === 1 || e.button === 2 || (e.button === 0 && s.tool === 'pan')) {
        d.type = 'pan';
        d.sx = e.clientX;
        d.sy = e.clientY;
        d.camX = cam2d.position.x;
        d.camY = cam2d.position.y;
        return;
      }

      // Measure / Set Scale: click-to-place-point mode
      if (e.button === 0 && (s.tool === 'measure' || s.tool === 'set-scale')) {
        d.type = 'click?';
        d.sx = e.clientX;
        d.sy = e.clientY;
        d.camX = cam2d.position.x;
        d.camY = cam2d.position.y;
        return;
      }

      // Draw or Subtract: click-to-place-point mode
      if (
        e.button === 0 &&
        (s.tool === 'draw' || s.tool === 'subtract' || s.tool === 'split' || s.tool === 'roundabout')
      ) {
        d.type = 'click?';
        d.sx = e.clientX;
        d.sy = e.clientY;
        d.camX = cam2d.position.x;
        d.camY = cam2d.position.y;
      }

      // Circle / Subtract-circle: click-drag mode
      if (e.button === 0 && (s.tool === 'circle' || s.tool === 'subtract-circle')) {
        const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc) return;
        const [sx2, sy2] = s.snapEnabled ? snapToGrid(wc[0], wc[1], s.gridSize) : wc;
        circleRef.current = { active: true, centerX: sx2, centerY: sy2 };
        d.type = 'circle-drag';
        d.sx = e.clientX;
        d.sy = e.clientY;
      }

      // Check if clicking near a hovered vertex of any polygon (select tool only)
      if (
        e.button === 0 &&
        s.tool === 'select' &&
        hoveredVertexRef.current.vertexIndex >= 0 &&
        s.drawingPoints.length === 0
      ) {
        const hv = hoveredVertexRef.current;
        const poly = s.polygons[hv.polygonIndex];
        if (poly) {
          // Make this polygon active if it isn't already
          if (s.activePolygonIndex !== hv.polygonIndex) {
            selectRef.current(hv.polygonIndex);
          }
          vertexDragRef.current = {
            active: true,
            polygonId: poly.id,
            polygonIndex: hv.polygonIndex,
            vertexIndex: hv.vertexIndex,
          };
          // Snapshot points before drag for undo
          dragStartSnapshotRef.current = {
            polygonId: poly.id,
            points: poly.points.map(([px, py]) => [px, py] as [number, number]),
          };
          d.type = 'vertex-drag';
          d.sx = e.clientX;
          d.sy = e.clientY;
          renderer.domElement.style.cursor = 'grabbing';
          return;
        }
      }

      if (e.button === 0 && s.tool === 'select') {
        // Start long-press timer for polygon move
        const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (wc) {
          const [wx, wy] = wc;
          // Check if inside an active polygon for long-press move
          if (s.activePolygonIndex !== null) {
            const poly = s.polygons[s.activePolygonIndex];
            if (poly && pointInPolygon(wx, wy, poly.points)) {
              longPressTimerRef.current = setTimeout(() => {
                // Snapshot points before drag for undo
                dragStartSnapshotRef.current = {
                  polygonId: poly.id,
                  points: poly.points.map(([px, py]) => [px, py] as [number, number]),
                };
                polygonDragRef.current = {
                  active: true,
                  polygonId: poly.id,
                  startWorld: [wx, wy],
                  originalPoints: poly.points.map(([px, py]) => [px, py] as [number, number]),
                };
                d.type = 'polygon-drag';
                renderer.domElement.style.cursor = 'move';
              }, 400);
            }
          }
        }
        d.type = 'select?';
        d.sx = e.clientX;
        d.sy = e.clientY;
      }
    };

    // ── POINTER MOVE ──
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      const s = stateRef.current;
      if (s.viewMode === '3d') return;

      // Image resize drag — dot product projection for pixel-perfect corner tracking
      if (d.type === 'image-resize' && imageResizeRef.current.active) {
        d.moved = true;
        const wc2 = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc2) return;
        const { startScale, startX, startY } = imageResizeRef.current;
        // Project mouse onto the corner vector from origin
        const dotProduct = wc2[0] * startX + wc2[1] * startY;
        const lenSq = startX * startX + startY * startY;
        if (lenSq === 0) return;
        const ratio = dotProduct / lenSq;
        const newScale = startScale * ratio;
        const clamped = Math.max(0.001, Math.min(20, newScale));

        // Synchronously update the mesh + overlays so they stay in sync every frame
        const tt = threeRef.current;
        const nat = imageNaturalRef.current;
        if (tt && nat.w > 0) {
          const iw = nat.w * clamped;
          const ih = nat.h * clamped;
          // Find the live mesh in the scene (ref can become stale after remounts)
          const liveMesh = tt.bgGroup.children.find((c): c is THREE.Mesh => (c as THREE.Mesh).isMesh);
          if (liveMesh) {
            liveMesh.scale.set(clamped, clamped, 1);
            liveMesh.position.set(iw / 2, ih / 2, -0.5);
            imageMeshRef.current = liveMesh; // keep ref fresh
          }
          updateImageOverlaysRef.current?.(tt, iw, ih);
        }

        // Also notify React so the prop stays in sync
        imageScaleChangeRef.current?.(clamped);
        return;
      }

      if (d.type === 'pan') {
        d.moved = true;
        const rect = el.getBoundingClientRect();
        const vw = cam2d.right - cam2d.left;
        const vh = cam2d.top - cam2d.bottom;
        cam2d.position.x = d.camX - ((e.clientX - d.sx) / rect.width) * vw;
        cam2d.position.y = d.camY + ((e.clientY - d.sy) / rect.height) * vh;
        cam2d.updateProjectionMatrix();
        return;
      }

      // Vertex drag — move vertex in real time
      if (d.type === 'vertex-drag' && vertexDragRef.current.active) {
        d.moved = true;
        const wc2 = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc2) return;
        const [sx2, sy2] = s.snapEnabled ? snapToGrid(wc2[0], wc2[1], s.gridSize) : wc2;
        const { polygonId, polygonIndex, vertexIndex } = vertexDragRef.current;
        const poly = s.polygons.find((p) => p.id === polygonId);
        if (poly) {
          const newPoints = [...poly.points] as [number, number][];
          newPoints[vertexIndex] = [sx2, sy2];

          // Immediately re-render this polygon in polyGroup for instant visual feedback
          const tRef = threeRef.current;
          if (tRef) {
            // Clear and re-render all polygons synchronously
            const res = resolutionRef.current;
            clearGroup(tRef.polyGroup);
            s.polygons.forEach((p2, i) => {
              const pts = i === polygonIndex ? newPoints : p2.points;
              const isActive = i === polygonIndex;
              const {
                stroke: strokeCol,
                fill: fillCol,
                fillOpacity: fillOpa,
                width: lineWidth,
              } = polygonStyle(isActive, selectedIdsRef.current.has(p2.id));

              const ring = [...pts, pts[0]] as [number, number][];
              tRef.polyGroup.add(makeLine2(ring, 0.2, strokeCol, lineWidth, res));
              const sh = buildShapeWithHoles(pts, p2.holes);
              tRef.polyGroup.add(
                new THREE.Mesh(
                  new THREE.ShapeGeometry(sh),
                  new THREE.MeshBasicMaterial({
                    color: fillCol,
                    transparent: true,
                    opacity: fillOpa,
                    side: THREE.DoubleSide,
                    depthWrite: false,
                    depthTest: false,
                  }),
                ),
              );
              pts.forEach(([x, y]) => {
                const vdot = new THREE.Mesh(
                  new THREE.CircleGeometry(1, 16),
                  new THREE.MeshBasicMaterial({
                    color: strokeCol,
                    depthWrite: false,
                    depthTest: false,
                    transparent: true,
                  }),
                );
                vdot.position.set(x, y, 0.2);
                vdot.userData.dotScreenPx = DOT_SCREEN_PX;
                tRef.polyGroup.add(vdot);
              });
            });

            // Drag highlight overlay — same size, yellow color only
            clearGroup(tRef.vertexOverlayGroup);
            const dot = new THREE.Mesh(
              new THREE.CircleGeometry(1, 16),
              new THREE.MeshBasicMaterial({ color: 0xffd600, depthWrite: false, depthTest: false, transparent: true }),
            );
            dot.position.set(sx2, sy2, 0.2);
            dot.userData.dotScreenPx = DOT_HOVER_SCREEN_PX;
            tRef.vertexOverlayGroup.add(dot);
          }

          // Update edge distance labels during drag
          const tEdge = threeRef.current;
          if (tEdge) {
            clearGroup(tEdge.edgeDistGroup);
            const edRes = resolutionRef.current;
            const metersPerUnit = s.metersPerUnit;
            const n = newPoints.length;
            const prevIdx = (vertexIndex - 1 + n) % n;
            const nextIdx = (vertexIndex + 1) % n;
            const [pvx, pvy] = newPoints[prevIdx];
            const [nvx, nvy] = newPoints[nextIdx];
            const edZ = 0.55;

            tEdge.edgeDistGroup.add(
              makeLine2(
                [
                  [pvx, pvy],
                  [sx2, sy2],
                ],
                edZ,
                0xd4ff33,
                2,
                edRes,
              ),
            );
            const dPrev = Math.hypot(sx2 - pvx, sy2 - pvy) * metersPerUnit;
            const pDiv = document.createElement('div');
            pDiv.style.cssText =
              'background:rgba(0,0,0,0.8);color:#d4ff33;padding:1px 5px;border-radius:3px;font-size:10px;font-weight:600;font-family:monospace;white-space:nowrap;pointer-events:none;';
            pDiv.textContent = dPrev < 1 ? `${(dPrev * 100).toFixed(1)} cm` : `${dPrev.toFixed(2)} m`;
            const pLabel = new CSS2DObject(pDiv);
            pLabel.position.set((pvx + sx2) / 2, (pvy + sy2) / 2, edZ);
            tEdge.edgeDistGroup.add(pLabel);

            tEdge.edgeDistGroup.add(
              makeLine2(
                [
                  [sx2, sy2],
                  [nvx, nvy],
                ],
                edZ,
                0xd4ff33,
                2,
                edRes,
              ),
            );
            const dNext = Math.hypot(nvx - sx2, nvy - sy2) * metersPerUnit;
            const nDiv = document.createElement('div');
            nDiv.style.cssText =
              'background:rgba(0,0,0,0.8);color:#d4ff33;padding:1px 5px;border-radius:3px;font-size:10px;font-weight:600;font-family:monospace;white-space:nowrap;pointer-events:none;';
            nDiv.textContent = dNext < 1 ? `${(dNext * 100).toFixed(1)} cm` : `${dNext.toFixed(2)} m`;
            const nLabel = new CSS2DObject(nDiv);
            nLabel.position.set((sx2 + nvx) / 2, (sy2 + nvy) / 2, edZ);
            tEdge.edgeDistGroup.add(nLabel);
          }

          // Update React state silently (no undo push) during drag
          if (silentUpdateRef.current) {
            silentUpdateRef.current(polygonId, newPoints);
          } else {
            updatePointsRef.current(polygonId, newPoints);
          }
        }
        renderer.domElement.style.cursor = 'grabbing';
        return;
      }

      // Polygon drag — move entire polygon
      if (d.type === 'polygon-drag' && polygonDragRef.current.active) {
        d.moved = true;
        const wc2 = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc2) return;
        const [wx, wy] = s.snapEnabled ? snapToGrid(wc2[0], wc2[1], s.gridSize) : wc2;
        const { polygonId, startWorld, originalPoints } = polygonDragRef.current;
        const dx = wx - startWorld[0];
        const dy = wy - startWorld[1];
        const newPoints = originalPoints.map(([px, py]) => [px + dx, py + dy] as [number, number]);
        // Update React state silently (no undo push) during drag
        if (silentUpdateRef.current) {
          silentUpdateRef.current(polygonId, newPoints);
        } else {
          updatePointsRef.current(polygonId, newPoints);
        }

        // Immediate visual update
        const tRef = threeRef.current;
        if (tRef) {
          const polyIdx = s.polygons.findIndex((p) => p.id === polygonId);
          if (polyIdx >= 0) {
            const res = resolutionRef.current;
            clearGroup(tRef.polyGroup);
            s.polygons.forEach((p2, i) => {
              const pts = i === polyIdx ? newPoints : p2.points;
              const isActive = i === polyIdx;
              const {
                stroke: strokeCol,
                fill: fillCol,
                fillOpacity: fillOpa,
                width: lineWidth,
              } = polygonStyle(isActive, selectedIdsRef.current.has(p2.id));
              const ring = [...pts, pts[0]] as [number, number][];
              tRef.polyGroup.add(makeLine2(ring, 0.2, strokeCol, lineWidth, res));
              const sh = buildShapeWithHoles(pts, p2.holes);
              tRef.polyGroup.add(
                new THREE.Mesh(
                  new THREE.ShapeGeometry(sh),
                  new THREE.MeshBasicMaterial({
                    color: fillCol,
                    transparent: true,
                    opacity: fillOpa,
                    side: THREE.DoubleSide,
                    depthWrite: false,
                    depthTest: false,
                  }),
                ),
              );
              pts.forEach(([x, y]) => {
                const vdot = new THREE.Mesh(
                  new THREE.CircleGeometry(1, 16),
                  new THREE.MeshBasicMaterial({
                    color: strokeCol,
                    depthWrite: false,
                    depthTest: false,
                    transparent: true,
                  }),
                );
                vdot.position.set(x, y, 0.2);
                vdot.userData.dotScreenPx = DOT_SCREEN_PX;
                tRef.polyGroup.add(vdot);
              });
            });
          }
        }
        renderer.domElement.style.cursor = 'move';
        return;
      }

      // Convert click?/select? to pan if dragged > 5px (cancel long-press timer)
      if (d.type === 'click?' || d.type === 'select?') {
        if (Math.hypot(e.clientX - d.sx, e.clientY - d.sy) > 5) {
          if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
          }
          d.type = 'pan';
          d.camX = cam2d.position.x;
          d.camY = cam2d.position.y;
          d.sx = e.clientX;
          d.sy = e.clientY;
          d.moved = true;
          return;
        }
      }

      // Update coordinate display + preview
      const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
      if (!wc) return;
      const [sx, sy] = s.snapEnabled ? snapToGrid(wc[0], wc[1], s.gridSize) : wc;
      lastMouseWorldRef.current = { x: sx, y: sy };
      coordChangeRef.current({ x: sx, y: sy });

      // ── Handle hover detection (image resize handles) — skip when scale is set ──
      const t0 = threeRef.current;
      let overResizeHandle = false;
      if (t0 && !s.scaleSet && t0.handleGroup.children.length > 0) {
        const vw = cam2d.right - cam2d.left;
        const hitR = vw * 0.02;
        for (const child of t0.handleGroup.children) {
          if (child.userData.isResizeHandle) {
            // Skip bottom-left (0,0) — it's the fixed anchor
            if (child.position.x === 0 && child.position.y === 0) continue;
            if (Math.hypot(wc[0] - child.position.x, wc[1] - child.position.y) < hitR) {
              overResizeHandle = true;
              break;
            }
          }
        }
        renderer.domElement.style.cursor = overResizeHandle ? 'nwse-resize' : '';
      }

      // ── Vertex hover detection (select tool only — draw/subtract tools never drag existing vertices) ──
      if (!overResizeHandle && t0 && s.tool === 'select' && s.drawingPoints.length === 0 && s.polygons.length > 0) {
        const vw = cam2d.right - cam2d.left;
        const hitRadius = vw * 0.015;
        let foundPoly = -1;
        let foundVertex = -1;
        // Check all polygons (top-to-bottom = last first)
        for (let pi = s.polygons.length - 1; pi >= 0; pi--) {
          const poly = s.polygons[pi];
          for (let vi = 0; vi < poly.points.length; vi++) {
            const [vx, vy] = poly.points[vi];
            if (Math.hypot(wc[0] - vx, wc[1] - vy) < hitRadius) {
              foundPoly = pi;
              foundVertex = vi;
              break;
            }
          }
          if (foundVertex >= 0) break;
        }
        const prev = hoveredVertexRef.current;
        if (foundPoly !== prev.polygonIndex || foundVertex !== prev.vertexIndex) {
          hoveredVertexRef.current = { polygonIndex: foundPoly, vertexIndex: foundVertex };
          clearGroup(t0.vertexOverlayGroup);
          clearGroup(t0.edgeDistGroup);
          if (foundVertex >= 0) {
            const poly = s.polygons[foundPoly];
            const [vx, vy] = poly.points[foundVertex];
            const dot = new THREE.Mesh(
              new THREE.CircleGeometry(1, 16),
              new THREE.MeshBasicMaterial({ color: 0xffd600, depthWrite: false, depthTest: false, transparent: true }),
            );
            dot.position.set(vx, vy, 0.2);
            dot.userData.dotScreenPx = DOT_HOVER_SCREEN_PX;
            t0.vertexOverlayGroup.add(dot);

            // Show edge distances to prev/next vertices
            const metersPerUnit = s.metersPerUnit;
            const edRes = resolutionRef.current;
            const n = poly.points.length;
            const prevIdx = (foundVertex - 1 + n) % n;
            const nextIdx = (foundVertex + 1) % n;
            const [px, py] = poly.points[prevIdx];
            const [nx2, ny2] = poly.points[nextIdx];
            const Z = 0.55;

            // Edge to previous
            t0.edgeDistGroup.add(
              makeLine2(
                [
                  [px, py],
                  [vx, vy],
                ],
                Z,
                0xd4ff33,
                2,
                edRes,
              ),
            );
            const distPrev = Math.hypot(vx - px, vy - py) * metersPerUnit;
            const prevDiv = document.createElement('div');
            prevDiv.style.cssText =
              'background:rgba(0,0,0,0.8);color:#d4ff33;padding:1px 5px;border-radius:3px;font-size:10px;font-weight:600;font-family:monospace;white-space:nowrap;pointer-events:none;';
            prevDiv.textContent = distPrev < 1 ? `${(distPrev * 100).toFixed(1)} cm` : `${distPrev.toFixed(2)} m`;
            const prevLabel = new CSS2DObject(prevDiv);
            prevLabel.position.set((px + vx) / 2, (py + vy) / 2, Z);
            t0.edgeDistGroup.add(prevLabel);

            // Edge to next
            t0.edgeDistGroup.add(
              makeLine2(
                [
                  [vx, vy],
                  [nx2, ny2],
                ],
                Z,
                0xd4ff33,
                2,
                edRes,
              ),
            );
            const distNext = Math.hypot(nx2 - vx, ny2 - vy) * metersPerUnit;
            const nextDiv = document.createElement('div');
            nextDiv.style.cssText =
              'background:rgba(0,0,0,0.8);color:#d4ff33;padding:1px 5px;border-radius:3px;font-size:10px;font-weight:600;font-family:monospace;white-space:nowrap;pointer-events:none;';
            nextDiv.textContent = distNext < 1 ? `${(distNext * 100).toFixed(1)} cm` : `${distNext.toFixed(2)} m`;
            const nextLabel = new CSS2DObject(nextDiv);
            nextLabel.position.set((vx + nx2) / 2, (vy + ny2) / 2, Z);
            t0.edgeDistGroup.add(nextLabel);
          }
          renderer.domElement.style.cursor = foundVertex >= 0 ? 'grab' : 'default';
        }
      } else if (!overResizeHandle && t0) {
        // Clear any stale vertex overlay and reset cursor for current tool
        if (hoveredVertexRef.current.vertexIndex !== -1) {
          hoveredVertexRef.current = { polygonIndex: -1, vertexIndex: -1 };
          clearGroup(t0.vertexOverlayGroup);
          clearGroup(t0.edgeDistGroup);
        }
        renderer.domElement.style.cursor =
          s.tool === 'draw' ||
          s.tool === 'subtract' ||
          s.tool === 'split' ||
          s.tool === 'circle' ||
          s.tool === 'subtract-circle' ||
          s.tool === 'roundabout' ||
          s.tool === 'measure' ||
          s.tool === 'set-scale'
            ? 'crosshair'
            : 'default';
      }

      // ── Set Scale preview (angle-snapped rubber-band) ──
      if (s.tool === 'set-scale' && s.scalePoints.length === 1) {
        const t = threeRef.current;
        if (t) {
          // Remove old preview
          const toRemove = t.scaleGroup.children.filter((c) => c.userData.scalePreview);
          toRemove.forEach((c) => {
            t.scaleGroup.remove(c);
            c.traverse((o: THREE.Object3D) => {
              const m = o as THREE.Mesh;
              m.geometry?.dispose();
            });
          });

          const origin = s.scalePoints[0];
          const Z = 0.8;
          const previewLine = makeLine2(
            [
              [origin[0], origin[1]],
              [sx, sy],
            ],
            Z,
            0xff0000,
            3,
            resolutionRef.current,
          );
          previewLine.userData.scalePreview = true;
          t.scaleGroup.add(previewLine);
        }
      }

      // ── Measurement preview (rubber-band) ──
      if (s.tool === 'measure' && s.measurementPoints.length > 0) {
        const t = threeRef.current;
        if (t) {
          // Remove old preview lines (tagged with userData.measurePreview)
          const toRemove = t.measureGroup.children.filter((c) => c.userData.measurePreview);
          toRemove.forEach((c) => {
            t.measureGroup.remove(c);
            c.traverse((o: THREE.Object3D) => {
              const m = o as THREE.Mesh;
              m.geometry?.dispose();
            });
          });

          const lastPt = s.measurementPoints[s.measurementPoints.length - 1];
          const Z = 0.6;
          const previewLine = makeDashedLine2(
            [
              [lastPt[0], lastPt[1]],
              [sx, sy],
            ],
            Z,
            0xd4ff33,
            2,
            0.5,
            resolutionRef.current,
          );
          previewLine.userData.measurePreview = true;
          t.measureGroup.add(previewLine);

          // Preview distance label
          const metersPerUnit = s.metersPerUnit;
          const dist = Math.hypot(sx - lastPt[0], sy - lastPt[1]) * metersPerUnit;
          const labelDiv = document.createElement('div');
          labelDiv.style.cssText =
            'background:rgba(212,255,51,0.7);color:#000;padding:2px 5px;border-radius:3px;font-size:10px;font-weight:600;font-family:monospace;white-space:nowrap;pointer-events:none;';
          labelDiv.textContent = dist < 1 ? `${(dist * 100).toFixed(1)} cm` : `${dist.toFixed(2)} m`;
          const previewLabel = new CSS2DObject(labelDiv);
          previewLabel.position.set((lastPt[0] + sx) / 2, (lastPt[1] + sy) / 2, Z);
          previewLabel.userData.measurePreview = true;
          t.measureGroup.add(previewLabel);
        }
      }

      // ── Draw / Subtract preview ──
      if ((s.tool === 'draw' || s.tool === 'subtract' || s.tool === 'split') && s.drawingPoints.length > 0) {
        const t = threeRef.current;
        if (!t) return;
        clearGroup(t.previewGroup);

        const isSubtract = s.tool === 'subtract' || s.tool === 'split';
        const lineColor = isSubtract ? 0xff8800 : 0xff2222;
        const fillColor = isSubtract ? 0xff6600 : 0xff0000;
        const fillOpacity = isSubtract ? PREVIEW_SUBTRACT_FILL_OPACITY : PREVIEW_FILL_OPACITY;

        const pts = s.drawingPoints;
        const allPts = [...pts.map(([px, py]) => new THREE.Vector3(px, py, 0.2)), new THREE.Vector3(sx, sy, 0.2)];
        if (pts.length >= 3) allPts.push(new THREE.Vector3(pts[0][0], pts[0][1], 0.2));

        if (allPts.length >= 2) {
          const pts2d = allPts.map((v) => [v.x, v.y] as [number, number]);
          t.previewGroup.add(makeLine2(pts2d, 0.2, lineColor, isSubtract ? 4 : 2, resolutionRef.current));
        }

        // Live distance label between last placed point and cursor
        {
          const lastPt = pts[pts.length - 1];
          const metersPerUnit = s.metersPerUnit;
          const dist = Math.hypot(sx - lastPt[0], sy - lastPt[1]) * metersPerUnit;
          if (dist > 1e-6) {
            const labelDiv = document.createElement('div');
            labelDiv.style.cssText =
              'background:rgba(212,255,51,0.85);color:#000;padding:2px 5px;border-radius:3px;font-size:10px;font-weight:600;font-family:monospace;white-space:nowrap;pointer-events:none;';
            labelDiv.textContent = dist < 1 ? `${(dist * 100).toFixed(1)} cm` : `${dist.toFixed(2)} m`;
            const previewLabel = new CSS2DObject(labelDiv);
            previewLabel.position.set((lastPt[0] + sx) / 2, (lastPt[1] + sy) / 2, 0.25);
            t.previewGroup.add(previewLabel);
          }
        }

        if (pts.length >= 3) {
          const shPts = [...pts, [sx, sy] as [number, number]];
          const sh = new THREE.Shape();
          sh.moveTo(shPts[0][0], shPts[0][1]);
          shPts.slice(1).forEach(([px, py]) => sh.lineTo(px, py));
          sh.closePath();
          t.previewGroup.add(
            new THREE.Mesh(
              new THREE.ShapeGeometry(sh),
              new THREE.MeshBasicMaterial({
                color: fillColor,
                transparent: true,
                opacity: fillOpacity,
                side: THREE.DoubleSide,
                depthWrite: false,
              }),
            ),
          );
        }

        // Vertex dots
        pts.forEach(([px, py]) => {
          const dot = new THREE.Mesh(
            new THREE.CircleGeometry(1, 16),
            new THREE.MeshBasicMaterial({ color: lineColor, depthWrite: false, depthTest: false, transparent: true }),
          );
          dot.position.set(px, py, 0.3);
          dot.userData.dotScreenPx = DOT_SCREEN_PX;
          t.previewGroup.add(dot);
        });
      }

      // ── Circle / Subtract-circle preview ──
      if ((s.tool === 'circle' || s.tool === 'subtract-circle') && circleRef.current.active) {
        const t = threeRef.current;
        if (!t) return;
        clearGroup(t.previewGroup);

        const { centerX, centerY } = circleRef.current;
        const radius = Math.hypot(sx - centerX, sy - centerY);

        // Color depends on tool: blue for circle, orange for subtract-circle
        const isSubCircle = s.tool === 'subtract-circle';
        const lineColor = isSubCircle ? 0xff8800 : 0x22aaff;
        const fillColor = isSubCircle ? 0xff6600 : 0x2288ff;
        const fillOpacity = isSubCircle ? PREVIEW_SUBTRACT_FILL_OPACITY : PREVIEW_FILL_OPACITY;

        if (radius > 0.01) {
          const segments = 48;

          // Circle outline
          const circlePts: THREE.Vector3[] = [];
          for (let i = 0; i <= segments; i++) {
            const angle = (2 * Math.PI * i) / segments;
            circlePts.push(
              new THREE.Vector3(centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius, 0.2),
            );
          }
          t.previewGroup.add(
            new THREE.Line(
              new THREE.BufferGeometry().setFromPoints(circlePts),
              new THREE.LineBasicMaterial({
                color: lineColor,
                transparent: true,
                opacity: 1.0,
                depthTest: false,
                depthWrite: false,
              }),
            ),
          );

          // Fill
          const shape = new THREE.Shape();
          for (let i = 0; i < segments; i++) {
            const angle = (2 * Math.PI * i) / segments;
            const px = centerX + Math.cos(angle) * radius;
            const py = centerY + Math.sin(angle) * radius;
            if (i === 0) shape.moveTo(px, py);
            else shape.lineTo(px, py);
          }
          shape.closePath();
          t.previewGroup.add(
            new THREE.Mesh(
              new THREE.ShapeGeometry(shape),
              new THREE.MeshBasicMaterial({
                color: fillColor,
                transparent: true,
                opacity: fillOpacity,
                side: THREE.DoubleSide,
                depthWrite: false,
              }),
            ),
          );

          // Center dot
          const dot = new THREE.Mesh(
            new THREE.CircleGeometry(1, 16),
            new THREE.MeshBasicMaterial({ color: lineColor, depthWrite: false, depthTest: false, transparent: true }),
          );
          dot.position.set(centerX, centerY, 0.2);
          dot.userData.dotScreenPx = DOT_SCREEN_PX;
          t.previewGroup.add(dot);

          // Radius line (center → cursor)
          t.previewGroup.add(
            new THREE.Line(
              new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(centerX, centerY, 0.2),
                new THREE.Vector3(sx, sy, 0.2),
              ]),
              new THREE.LineBasicMaterial({
                color: lineColor,
                transparent: true,
                opacity: 0.5,
                depthTest: false,
                depthWrite: false,
              }),
            ),
          );
        }
      }

      // ── Roundabout preview (3-click: center → inner → outer) ──
      if (s.tool === 'roundabout' && s.drawingPoints.length > 0) {
        const t = threeRef.current;
        if (!t) return;
        clearGroup(t.previewGroup);

        const lineColor = 0x22aaff;
        const fillColor = 0x2288ff;
        const segments = 48;
        const [cx, cy] = s.drawingPoints[0];

        const drawRing = (radius: number, opacity: number, dashed = false) => {
          if (radius <= 0.001) return;
          const pts: THREE.Vector3[] = [];
          for (let i = 0; i <= segments; i++) {
            const angle = (2 * Math.PI * i) / segments;
            pts.push(new THREE.Vector3(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius, 0.2));
          }
          const mat = dashed
            ? new THREE.LineDashedMaterial({
                color: lineColor,
                transparent: true,
                opacity,
                dashSize: Math.max(radius * 0.05, 0.5),
                gapSize: Math.max(radius * 0.03, 0.3),
                depthTest: false,
                depthWrite: false,
              })
            : new THREE.LineBasicMaterial({
                color: lineColor,
                transparent: true,
                opacity,
                depthTest: false,
                depthWrite: false,
              });
          const geom = new THREE.BufferGeometry().setFromPoints(pts);
          const line = new THREE.Line(geom, mat);
          if (dashed) line.computeLineDistances();
          t.previewGroup.add(line);
        };

        // Center dot
        const dot = new THREE.Mesh(
          new THREE.CircleGeometry(1, 16),
          new THREE.MeshBasicMaterial({ color: lineColor, depthWrite: false, depthTest: false, transparent: true }),
        );
        dot.position.set(cx, cy, 0.2);
        dot.userData.dotScreenPx = DOT_SCREEN_PX;
        t.previewGroup.add(dot);

        if (s.drawingPoints.length === 1) {
          // Following cursor — show inner ring preview
          const innerR = Math.hypot(sx - cx, sy - cy);
          drawRing(innerR, 1.0);
        } else if (s.drawingPoints.length === 2) {
          // Inner is fixed, outer follows cursor — draw annulus fill
          const innerR = Math.hypot(s.drawingPoints[1][0] - cx, s.drawingPoints[1][1] - cy);
          const outerR = Math.hypot(sx - cx, sy - cy);
          drawRing(innerR, 1.0);
          drawRing(outerR, 1.0, true);

          if (outerR > innerR && innerR > 0.001) {
            // Annulus fill via shape with hole
            const shape = new THREE.Shape();
            for (let i = 0; i < segments; i++) {
              const angle = (2 * Math.PI * i) / segments;
              const px = cx + Math.cos(angle) * outerR;
              const py = cy + Math.sin(angle) * outerR;
              if (i === 0) shape.moveTo(px, py);
              else shape.lineTo(px, py);
            }
            shape.closePath();
            const holePath = new THREE.Path();
            for (let i = 0; i < segments; i++) {
              const angle = (2 * Math.PI * i) / segments;
              const px = cx + Math.cos(angle) * innerR;
              const py = cy + Math.sin(angle) * innerR;
              if (i === 0) holePath.moveTo(px, py);
              else holePath.lineTo(px, py);
            }
            holePath.closePath();
            shape.holes.push(holePath);
            t.previewGroup.add(
              new THREE.Mesh(
                new THREE.ShapeGeometry(shape),
                new THREE.MeshBasicMaterial({
                  color: fillColor,
                  transparent: true,
                  opacity: PREVIEW_FILL_OPACITY,
                  side: THREE.DoubleSide,
                  depthWrite: false,
                }),
              ),
            );
          }
        }
      }
    };

    // ── POINTER UP ──
    const onUp = (e: PointerEvent) => {
      const d = dragRef.current;
      const was = d.type;
      d.type = null;
      const s = stateRef.current;

      // Cancel long-press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      // Image resize — finalize
      if (was === 'image-resize') {
        imageResizeRef.current.active = false;
        renderer.domElement.style.cursor = 'default';
        return;
      }

      // Vertex drag — finalize
      if (was === 'vertex-drag') {
        vertexDragRef.current.active = false;
        hoveredVertexRef.current = { polygonIndex: -1, vertexIndex: -1 };
        const tRef = threeRef.current;
        if (tRef) {
          clearGroup(tRef.vertexOverlayGroup);
          clearGroup(tRef.edgeDistGroup);
        }
        // Push single undo command for entire drag
        if (dragStartSnapshotRef.current && pushUndoRef.current) {
          pushUndoRef.current(dragStartSnapshotRef.current.polygonId, dragStartSnapshotRef.current.points);
          dragStartSnapshotRef.current = null;
        }
        renderer.domElement.style.cursor = 'default';
        return;
      }

      // Polygon drag — finalize
      if (was === 'polygon-drag') {
        polygonDragRef.current.active = false;
        // Push single undo command for entire drag
        if (dragStartSnapshotRef.current && pushUndoRef.current) {
          pushUndoRef.current(dragStartSnapshotRef.current.polygonId, dragStartSnapshotRef.current.points);
          dragStartSnapshotRef.current = null;
        }
        renderer.domElement.style.cursor = 'default';
        return;
      }

      // Set Scale — place calibration point (max 2)
      if (was === 'click?' && !d.moved && s.viewMode === '2d' && s.tool === 'set-scale') {
        const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc) return;
        const [sx, sy] = wc;
        if (s.scalePoints.length < 2) {
          addScalePointRef.current?.([sx, sy]);
        }
        return;
      }

      // Measure — place point
      if (was === 'click?' && !d.moved && s.viewMode === '2d' && s.tool === 'measure') {
        const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc) return;
        const [sx, sy] = s.snapEnabled ? snapToGrid(wc[0], wc[1], s.gridSize) : wc;
        addMeasureRef.current?.([sx, sy]);
        return;
      }

      // Draw / Subtract — place point or close polygon
      if (
        was === 'click?' &&
        !d.moved &&
        s.viewMode === '2d' &&
        (s.tool === 'draw' || s.tool === 'subtract' || s.tool === 'split')
      ) {
        const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc) return;
        const [sx, sy] = s.snapEnabled ? snapToGrid(wc[0], wc[1], s.gridSize) : wc;
        const cur = s.drawingPoints;

        // Auto-close on proximity to the first point was removed because it
        // closed polygons users were still drawing. Closing now requires an
        // explicit action: Enter or double-click.
        drawingChangeRef.current([...cur, [sx, sy]]);
      }

      // Roundabout — 3-click flow: center, inner radius, outer radius
      if (was === 'click?' && !d.moved && s.viewMode === '2d' && s.tool === 'roundabout') {
        const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc) return;
        const [sx, sy] = s.snapEnabled ? snapToGrid(wc[0], wc[1], s.gridSize) : wc;
        const cur = s.drawingPoints;
        const next = [...cur, [sx, sy] as [number, number]];

        if (next.length < 3) {
          drawingChangeRef.current(next);
          return;
        }

        // Third click — finalize as outer ring with inner hole
        const [cx, cy] = next[0];
        const innerR = Math.hypot(next[1][0] - cx, next[1][1] - cy);
        const outerR = Math.hypot(next[2][0] - cx, next[2][1] - cy);
        const t2 = threeRef.current;

        if (innerR < 0.01 || outerR <= innerR) {
          // Invalid geometry — abort and let the user retry
          drawingChangeRef.current([]);
          if (t2) clearGroup(t2.previewGroup);
          return;
        }

        const outerPts = circlePoints(cx, cy, outerR, 48);
        const innerPts = circlePoints(cx, cy, innerR, 48);
        completeRef.current(outerPts, [innerPts]);
        drawingChangeRef.current([]);
        if (t2) clearGroup(t2.previewGroup);
      }

      // Circle — complete on release
      if (was === 'circle-drag' && circleRef.current.active) {
        const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc) return;
        const [sx2, sy2] = s.snapEnabled ? snapToGrid(wc[0], wc[1], s.gridSize) : wc;
        const { centerX, centerY } = circleRef.current;
        const radius = Math.hypot(sx2 - centerX, sy2 - centerY);

        circleRef.current.active = false;

        // Minimum radius threshold to prevent accidental tiny circles
        const vw = cam2d.right - cam2d.left;
        if (radius < vw * 0.005) {
          const t2 = threeRef.current;
          if (t2) clearGroup(t2.previewGroup);
          return;
        }

        const points = circlePoints(centerX, centerY, radius, 48);
        completeRef.current(points);
        const t3 = threeRef.current;
        if (t3) clearGroup(t3.previewGroup);
      }

      // Select — via select tool
      if (was === 'select?' && !d.moved && s.viewMode === '2d' && s.tool === 'select') {
        const wc = screenToWorld(e.clientX, e.clientY, cam2d, renderer.domElement);
        if (!wc) return;
        const [wx, wy] = wc;
        // Hit test: find polygon containing the point
        for (let i = s.polygons.length - 1; i >= 0; i--) {
          if (pointInPolygon(wx, wy, s.polygons[i].points)) {
            // Shift/⌘/Ctrl + click ticks it alongside others; a plain click picks only this one.
            if ((e.shiftKey || e.metaKey || e.ctrlKey) && toggleSelectedRef.current) {
              toggleSelectedRef.current(s.polygons[i].id);
            } else {
              selectRef.current(i);
            }
            return;
          }
        }
      }
    };

    const onDbl = () => {
      const s = stateRef.current;
      if (s.drawingPoints.length >= 3) {
        completeRef.current(s.drawingPoints);
        const t2 = threeRef.current;
        if (t2) clearGroup(t2.previewGroup);
      }
    };

    const dom = renderer.domElement;

    // ── TWO-FINGER ZOOM AND PAN (phones, tablets) ──
    // Runs before the handlers below (capture); while two fingers are down it keeps the gesture
    // from placing points or dragging, and the first finger's touch no longer counts as a click.
    const touches = new Map<number, { x: number; y: number }>();
    let pinch: { dist: number; midX: number; midY: number } | null = null;
    const pinchState = () => {
      const [a, b] = [...touches.values()];
      return { dist: Math.hypot(a.x - b.x, a.y - b.y), midX: (a.x + b.x) / 2, midY: (a.y + b.y) / 2 };
    };
    const onTouchDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.size === 2 && stateRef.current.viewMode === '2d') {
        pinch = pinchState();
        dragRef.current.moved = true;
        e.stopImmediatePropagation();
      }
    };
    const onTouchMove = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !touches.has(e.pointerId)) return;
      touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (!pinch || touches.size < 2) return;
      e.stopImmediatePropagation();
      const next = pinchState();
      if (next.dist > 0) zoomAt(next.midX, next.midY, pinch.dist / next.dist);
      const rect = el.getBoundingClientRect();
      cam2d.position.x -= ((next.midX - pinch.midX) / rect.width) * (cam2d.right - cam2d.left);
      cam2d.position.y += ((next.midY - pinch.midY) / rect.height) * (cam2d.top - cam2d.bottom);
      cam2d.updateProjectionMatrix();
      pinch = next;
    };
    const onTouchUp = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      touches.delete(e.pointerId);
      if (!pinch) return;
      dragRef.current.moved = true;
      if (touches.size === 0) pinch = null;
    };
    dom.addEventListener('pointerdown', onTouchDown, { capture: true });
    dom.addEventListener('pointermove', onTouchMove, { capture: true });
    dom.addEventListener('pointerup', onTouchUp, { capture: true });
    dom.addEventListener('pointercancel', onTouchUp, { capture: true });

    dom.addEventListener('pointerdown', onDown);
    dom.addEventListener('pointermove', onMove);
    dom.addEventListener('pointerup', onUp);
    dom.addEventListener('dblclick', onDbl);
    dom.addEventListener('contextmenu', (e: Event) => e.preventDefault());

    // ── RESIZE ──
    const onResize = () => {
      const w2 = el.clientWidth;
      const h2 = el.clientHeight;
      if (!w2 || !h2) return;
      const a2 = w2 / h2;
      const hh = (cam2d.top - cam2d.bottom) / 2;
      cam2d.left = -hh * a2;
      cam2d.right = hh * a2;
      cam2d.updateProjectionMatrix();
      zoomChangeRef.current?.(cam2d.right - cam2d.left);
      cam3d.aspect = a2;
      cam3d.updateProjectionMatrix();
      renderer.setSize(w2, h2);
      css2dRenderer.setSize(w2, h2);
      resolutionRef.current.set(w2, h2);
    };
    window.addEventListener('resize', onResize);
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(el);

    // ── KEYBOARD ──
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack keys while the user is typing into an input / textarea / contentEditable element.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return;
        }
      }
      const s = stateRef.current;
      if (e.key === 'Escape') {
        const hadTransient =
          s.drawingPoints.length > 0 ||
          circleRef.current.active ||
          s.measurementPoints.length > 0 ||
          s.scalePoints.length > 0;

        drawingChangeRef.current([]);
        circleRef.current.active = false;
        clearMeasureRef.current?.();
        clearScalePointsRef.current?.();
        const t = threeRef.current;
        if (t) {
          clearGroup(t.previewGroup);
          clearGroup(t.measureGroup);
          clearGroup(t.scaleGroup);
        }

        // Progressive Escape: cancel drawing/measure/scale first; otherwise deselect.
        if (!hadTransient && s.activePolygonIndex !== null) {
          selectRef.current(null);
        }
      }
      if (e.key === 'Enter' && s.drawingPoints.length >= 3) {
        completeRef.current(s.drawingPoints);
        const t2 = threeRef.current;
        if (t2) clearGroup(t2.previewGroup);
      }
      if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (s.drawingPoints.length > 0) {
          drawingChangeRef.current(s.drawingPoints.slice(0, -1));
        }
      }
      // Arrow keys pan
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) && s.viewMode === '2d') {
        e.preventDefault();
        const viewW = cam2d.right - cam2d.left;
        let step = viewW / 8;
        if (e.shiftKey) step = viewW / 40;
        if (e.ctrlKey) step = viewW / 3;
        if (e.key === 'ArrowLeft') cam2d.position.x -= step;
        if (e.key === 'ArrowRight') cam2d.position.x += step;
        if (e.key === 'ArrowUp') cam2d.position.y += step;
        if (e.key === 'ArrowDown') cam2d.position.y -= step;
        cam2d.updateProjectionMatrix();
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKey);
      resizeObserver.disconnect();
      dom.removeEventListener('wheel', onWheel);
      cancelAnimationFrame(threeRef.current?.animId ?? 0);
      cadGrid.dispose();
      renderer.dispose();
      if (el.contains(dom)) el.removeChild(dom);
      if (el.contains(css2dRenderer.domElement)) el.removeChild(css2dRenderer.domElement);
      threeRef.current = null;
    };
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  //  SYNC DXF DATA
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;
    // Only the drawing's lines: the plan image shares the group and has its own effect.
    clearGroup(t.bgGroup, isDrawingLines);
    if (!dxfData) return;

    // One line object per layer (a city tile has ~90,000 entities; one object each would be as many
    // draw calls). The layer name is what hover highlighting keys on.
    const segmentsByLayer = new Map<string, number[]>();
    dxfData.entities.forEach((entity) => {
      const v = entity.vertices;
      if (v.length < 2) return;
      let segments = segmentsByLayer.get(entity.layer);
      if (!segments) segmentsByLayer.set(entity.layer, (segments = []));
      const count = entity.closed && v.length >= 3 ? v.length : v.length - 1;
      for (let i = 0; i < count; i++) {
        const [x0, y0] = v[i];
        const [x1, y1] = v[(i + 1) % v.length];
        segments.push(x0, y0, 0, x1, y1, 0);
      }
    });
    segmentsByLayer.forEach((segments, layer) => {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(segments, 3));
      // The same colour the hover highlight returns to.
      const mat = new THREE.LineBasicMaterial({ color: 0x6699bb, transparent: true, opacity: 0.7 });
      const lines = new THREE.LineSegments(geo, mat);
      lines.userData.layerName = layer;
      t.bgGroup.add(lines);
    });

    // Fit camera to DXF bounds
    const box = new THREE.Box3().setFromObject(t.bgGroup);
    if (!box.isEmpty()) {
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      fitCamera(center.x, center.y, Math.max(size.x, size.y, 10) * 1.3);
    }
  }, [dxfData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ══════════════════════════════════════════════════════════════════════════
  //  HIGHLIGHT LAYER ON HOVER
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;

    t.bgGroup.children.forEach((child) => {
      const line = child as THREE.Line;
      const mat = line.material as THREE.LineBasicMaterial;
      if (!mat?.isMaterial) return;

      const layerName = line.userData.layerName as string | undefined;
      if (!layerName) return;

      if (!highlightedLayer) {
        // No hover — reset to default
        mat.color.setHex(0x6699bb);
        mat.opacity = 0.7;
      } else if (layerName === highlightedLayer) {
        // Highlighted layer — bright lime
        mat.color.setHex(0xb3ec49);
        mat.opacity = 1;
      } else {
        // Other layers — dim
        mat.color.setHex(0x334455);
        mat.opacity = 0.25;
      }
    });
  }, [highlightedLayer]);

  // ══════════════════════════════════════════════════════════════════════════
  //  SYNC IMAGE — load once when URL changes
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;
    // Only the image: the imported drawing's lines share the group and have their own effect.
    if (!imageUrl) {
      clearGroup(t.bgGroup, isImageMesh);
      clearGroup(t.handleGroup);
      imageMeshRef.current = null;
      imageNaturalRef.current = { w: 0, h: 0 };
      return;
    }
    clearGroup(t.bgGroup, isImageMesh);
    clearGroup(t.handleGroup);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const cvs = document.createElement('canvas');
      cvs.width = img.naturalWidth;
      cvs.height = img.naturalHeight;
      const ctx = cvs.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);

      const tex = new THREE.CanvasTexture(cvs);
      tex.minFilter = THREE.LinearFilter;
      tex.needsUpdate = true;

      // Store natural dimensions
      imageNaturalRef.current = { w: cvs.width, h: cvs.height };

      // Create mesh at unit natural size — scale is applied via mesh.scale
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(cvs.width, cvs.height),
        new THREE.MeshBasicMaterial({
          map: tex,
          side: THREE.DoubleSide,
          transparent: true,
          depthWrite: false,
          depthTest: false,
        }),
      );
      mesh.renderOrder = -10;
      // Geometry is centered at origin; offset so bottom-left = (0,0) at scale 1
      // position = (w/2, h/2) before scale; we use the geometry as-is and set position in the scale effect
      mesh.position.set(0, 0, -0.5);
      t.bgGroup.add(mesh);
      imageMeshRef.current = mesh;

      // Create persistent handles (positions updated in scale effect)
      const handleGeo = new THREE.CircleGeometry(1, 12);
      const handleMat = new THREE.MeshBasicMaterial({
        color: 0xd4ff33,
        depthWrite: false,
        depthTest: false,
        transparent: true,
      });
      for (let idx = 0; idx < 4; idx++) {
        const handle = new THREE.Mesh(handleGeo, handleMat);
        handle.position.set(0, 0, 0.7);
        handle.renderOrder = 20;
        handle.userData.isResizeHandle = true;
        handle.userData.cornerIndex = idx;
        handle.userData.dotScreenPx = 4;
        t.handleGroup.add(handle);
      }

      // Trigger the scale effect by updating imageScale to itself (initial layout)
      const s = stateRef.current.imageScale;
      const iw = cvs.width * s;
      const ih = cvs.height * s;
      // Position mesh so bottom-left corner = (0,0)
      mesh.scale.set(s, s, 1);
      mesh.position.set(iw / 2, ih / 2, -0.5);
      // Update handles & border inline for first render
      updateImageOverlays(t, iw, ih);
      fitCamera(iw / 2, ih / 2, Math.max(iw, ih) * 1.15);
    };
    img.src = imageUrl;
  }, [imageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // ══════════════════════════════════════════════════════════════════════════
  //  UPDATE IMAGE SCALE — fast, no mesh recreation
  // ══════════════════════════════════════════════════════════════════════════

  /** Update border line and handle positions for current scaled image size. */
  const updateImageOverlays = useCallback((t: NonNullable<typeof threeRef.current>, iw: number, ih: number) => {
    // Remove old border (first child if it's a Line2)
    const oldBorder = t.handleGroup.children.find((c) => !c.userData.isResizeHandle);
    if (oldBorder) {
      t.handleGroup.remove(oldBorder);
      oldBorder.traverse((o: THREE.Object3D) => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose();
      });
    }

    // New border
    const borderPts: [number, number][] = [
      [0, 0],
      [iw, 0],
      [iw, ih],
      [0, ih],
      [0, 0],
    ];
    const border = makeLine2(borderPts, 0.2, 0xd4ff33, 2, resolutionRef.current);
    border.renderOrder = 15;
    t.handleGroup.add(border);

    // Update handle positions
    const corners: [number, number][] = [
      [0, 0],
      [iw, 0],
      [iw, ih],
      [0, ih],
    ];
    for (const child of t.handleGroup.children) {
      if (child.userData.isResizeHandle) {
        const ci = child.userData.cornerIndex as number;
        const [hx, hy] = corners[ci];
        child.position.set(hx, hy, 0.7);
      }
    }
  }, []);

  // Keep the ref in sync so pointer-move can call it synchronously
  updateImageOverlaysRef.current = updateImageOverlays;

  useEffect(() => {
    const t = threeRef.current;
    const { w, h } = imageNaturalRef.current;
    if (!t || w === 0) return;

    const iw = w * imageScale;
    const ih = h * imageScale;

    // Always find the live mesh in the scene to avoid stale ref issues
    const liveMesh = t.bgGroup.children.find((c): c is THREE.Mesh => (c as THREE.Mesh).isMesh);
    if (liveMesh) {
      liveMesh.scale.set(imageScale, imageScale, 1);
      liveMesh.position.set(iw / 2, ih / 2, -0.5);
      imageMeshRef.current = liveMesh; // keep ref fresh
    }

    // Update border + handles
    updateImageOverlays(t, iw, ih);
  }, [imageScale, updateImageOverlays]);

  // ══════════════════════════════════════════════════════════════════════════
  //  SYNC POLYGONS (2D)
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;
    clearGroup(t.polyGroup);
    clearGroup(t.vertexOverlayGroup);
    hoveredVertexRef.current = { polygonIndex: -1, vertexIndex: -1 };

    const res = resolutionRef.current;
    polygons.forEach((p, i) => {
      const isActive = i === activePolygonIndex;
      const {
        stroke: strokeCol,
        fill: fillCol,
        fillOpacity: fillOpa,
        width: lineWidth,
      } = polygonStyle(isActive, selectedIdsRef.current.has(p.id));

      // Outline — main ring (Line2 for thickness)
      const ring = [...p.points, p.points[0]] as [number, number][];
      t.polyGroup.add(makeLine2(ring, 0.2, strokeCol, lineWidth, res));

      // Outline — hole rings
      if (p.holes) {
        for (const hole of p.holes) {
          const holeRing = [...hole, hole[0]] as [number, number][];
          t.polyGroup.add(makeLine2(holeRing, 0.2, strokeCol, lineWidth, res));
        }
      }

      // Fill (with holes)
      const sh = buildShapeWithHoles(p.points, p.holes);
      t.polyGroup.add(
        new THREE.Mesh(
          new THREE.ShapeGeometry(sh),
          new THREE.MeshBasicMaterial({
            color: fillCol,
            transparent: true,
            opacity: fillOpa,
            side: THREE.DoubleSide,
            depthWrite: false,
            depthTest: false,
          }),
        ),
      );

      // Vertex dots — unit circle, scaled in animation loop
      p.points.forEach(([x, y]) => {
        const dot = new THREE.Mesh(
          new THREE.CircleGeometry(1, 16),
          new THREE.MeshBasicMaterial({ color: strokeCol, depthWrite: false, depthTest: false, transparent: true }),
        );
        dot.position.set(x, y, 0.2);
        dot.userData.dotScreenPx = DOT_SCREEN_PX;
        t.polyGroup.add(dot);
      });
    });
  }, [polygons, activePolygonIndex, selectedPolygonIds]);

  // ══════════════════════════════════════════════════════════════════════════
  //  SYNC 3D EXTRUSIONS
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;
    clearGroup(t.extGroup);

    if (viewMode !== '3d') return;

    polygons.forEach((p) => {
      if (p.height < 0) return;

      const sh = buildShapeWithHoles(p.points, p.holes);

      // height=0 → flat plane, otherwise extrude. Heights are metres, the plan is in drawing units.
      const geo =
        p.height === 0
          ? new THREE.ShapeGeometry(sh)
          : new THREE.ExtrudeGeometry(sh, { depth: p.height / metersPerUnit, bevelEnabled: false });
      t.extGroup.add(
        new THREE.Mesh(
          geo,
          new THREE.MeshPhongMaterial({
            color: POLY_FILL,
            transparent: true,
            opacity: 0.65,
            side: THREE.DoubleSide,
            flatShading: true,
          }),
        ),
      );
      t.extGroup.add(
        new THREE.LineSegments(
          new THREE.EdgesGeometry(geo),
          new THREE.LineBasicMaterial({ color: POLY_STROKE, transparent: true, opacity: 0.6 }),
        ),
      );
    });
  }, [polygons, viewMode, metersPerUnit]);

  // ══════════════════════════════════════════════════════════════════════════
  //  SYNC VIEW MODE (enable/disable OrbitControls)
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;

    t.controls3d.enabled = viewMode === '3d';

    // Hide 2D overlays in 3D mode, show in 2D
    t.polyGroup.visible = viewMode === '2d';
    t.vertexOverlayGroup.visible = viewMode === '2d';
    t.edgeDistGroup.visible = viewMode === '2d';
    t.handleGroup.visible = viewMode === '2d' && !scaleSet;
    t.bboxGroup.visible = viewMode === '2d';
    t.measureGroup.visible = viewMode === '2d';

    if (viewMode === '3d') {
      // Fit camera to polygons/extrusions only — NOT the scene (grid spans ±2000 units)
      // extGroup is populated by the SYNC 3D EXTRUSIONS effect which runs first (same dep change)
      const targetGroup = t.extGroup.children.length > 0 ? t.extGroup : t.polyGroup;
      const box = new THREE.Box3().setFromObject(targetGroup);

      if (!box.isEmpty()) {
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // Compute distance so the largest dimension fits in view
        // cam3d.fov is the vertical FOV in degrees
        const fovHalfRad = (t.cam3d.fov / 2) * (Math.PI / 180);
        const maxDim = Math.max(size.x, size.y, size.z, 1);
        const dist = (maxDim / 2 / Math.tan(fovHalfRad)) * 1.4; // ×1.4 → slight padding

        // Isometric-ish angle: 45° around Z, 35° elevation
        t.cam3d.position.set(center.x + dist * 0.7, center.y - dist * 0.7, center.z + dist * 0.6);
        t.cam3d.lookAt(center);
        t.controls3d.target.copy(center);
        t.controls3d.update();
      }
      // Clear preview while in 3D
      clearGroup(t.previewGroup);
    }
  }, [viewMode, scaleSet]);

  // ── Fit camera helper ──
  const fitCamera = useCallback((cx: number, cy: number, sz: number) => {
    const t = threeRef.current;
    const el = containerRef.current;
    if (!t || !el) return;
    const asp = el.clientWidth / el.clientHeight;
    const h = sz / 2;
    t.cam2d.left = -h * asp;
    t.cam2d.right = h * asp;
    t.cam2d.top = h;
    t.cam2d.bottom = -h;
    t.cam2d.position.set(cx, cy, 100);
    t.cam2d.quaternion.identity();
    t.cam2d.updateProjectionMatrix();
    zoomChangeRef.current?.(t.cam2d.right - t.cam2d.left);
  }, []);

  // ── Capture function for preview image ──
  useEffect(() => {
    captureRef.current = () => {
      const t = threeRef.current;
      if (!t) return '';

      // Hide ALL interactive/editor elements for a clean capture
      const prevBg = t.scene.background;
      t.scene.background = null;
      t.cadGrid.group.visible = false;
      t.previewGroup.visible = false;
      t.polyGroup.visible = false; // hide editor polygons (have selection colors, vertex dots)
      t.vertexOverlayGroup.visible = false;
      t.handleGroup.visible = false; // hide yellow image border & resize handles
      t.rulerGroup.visible = false; // hide unit ruler
      t.measureGroup.visible = false; // hide measurement lines/labels
      t.scaleGroup.visible = false; // hide scale calibration markers
      t.bboxGroup.visible = false; // hide light-blue selection bounding box + dimension labels

      // Build temp clean polygon outlines + fills with consistent (non-selected) colors
      const capturePolyGroup = new THREE.Group();
      const res = resolutionRef.current;
      polygons.forEach((p) => {
        // Outline — main ring
        const ring = [...p.points, p.points[0]] as [number, number][];
        capturePolyGroup.add(makeLine2(ring, 0.2, POLY_STROKE, 2, res));
        // Outline — holes
        if (p.holes) {
          for (const hole of p.holes) {
            const holeRing = [...hole, hole[0]] as [number, number][];
            capturePolyGroup.add(makeLine2(holeRing, 0.2, POLY_STROKE, 2, res));
          }
        }
        // Fill
        const sh = buildShapeWithHoles(p.points, p.holes);
        capturePolyGroup.add(
          new THREE.Mesh(
            new THREE.ShapeGeometry(sh),
            new THREE.MeshBasicMaterial({
              color: POLY_FILL,
              transparent: true,
              opacity: POLY_FILL_OPACITY,
              side: THREE.DoubleSide,
              depthWrite: false,
              depthTest: false,
            }),
          ),
        );
      });
      t.scene.add(capturePolyGroup);

      // Build temp extrusions for all polygons (use minimum height if 0)
      const wasEmpty = t.extGroup.children.length === 0;
      if (wasEmpty) {
        polygons.forEach((p) => {
          // Metres to drawing units, like the 3D view.
          const captureHeight = Math.max(p.height, 1) / metersPerUnit;
          const sh = buildShapeWithHoles(p.points, p.holes);
          const geo = new THREE.ExtrudeGeometry(sh, { depth: captureHeight, bevelEnabled: false });
          t.extGroup.add(
            new THREE.Mesh(
              geo,
              new THREE.MeshPhongMaterial({
                color: POLY_FILL,
                transparent: true,
                opacity: 0.65,
                side: THREE.DoubleSide,
                flatShading: true,
              }),
            ),
          );
        });
      }

      // Position camera straight above looking down (Z+ → top-down 2D view).
      // Include `bgGroup` (uploaded reference image) so the framing fits both the
      // model extrusions and the plan image behind them.
      const box = new THREE.Box3().setFromObject(t.extGroup);
      if (t.bgGroup.children.length > 0) {
        box.expandByObject(t.bgGroup);
      }
      if (!box.isEmpty()) {
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // Use an orthographic camera for a clean top-down capture
        const aspect = t.renderer.domElement.width / t.renderer.domElement.height;
        const maxDim = Math.max(size.x, size.y, 1) * 1.3; // 30% padding
        const halfH = maxDim / 2;
        const halfW = halfH * aspect;
        const captureCam = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, 0.1, 50000);
        // Position above the center, looking straight down
        captureCam.position.set(center.x, center.y, center.z + size.z + 100);
        captureCam.up.set(0, 1, 0);
        captureCam.lookAt(center);
        captureCam.updateProjectionMatrix();

        t.renderer.render(t.scene, captureCam);
      } else {
        t.renderer.render(t.scene, t.cam3d);
      }
      const dataUrl = t.renderer.domElement.toDataURL('image/png', 1.0);

      // Restore
      t.scene.remove(capturePolyGroup);
      capturePolyGroup.traverse((o: THREE.Object3D) => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose();
      });
      if (wasEmpty) clearGroup(t.extGroup);
      t.scene.background = prevBg;
      t.cadGrid.group.visible = true;
      t.bgGroup.visible = true;
      t.previewGroup.visible = true;
      t.polyGroup.visible = true;
      t.vertexOverlayGroup.visible = true;
      t.handleGroup.visible = !stateRef.current.scaleSet;
      t.rulerGroup.visible = true;
      t.measureGroup.visible = true;
      t.scaleGroup.visible = true;
      t.bboxGroup.visible = true;

      return dataUrl.split(',')[1]; // base64 only
    };
  }, [polygons, captureRef, metersPerUnit]);

  // ══════════════════════════════════════════════════════════════════════════
  //  UNIT RULER — fixed line in scene at (0, -2) → (1, -2) showing current unit
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;
    clearGroup(t.rulerGroup);

    const label =
      metersPerUnit >= 1
        ? `${parseFloat(metersPerUnit.toFixed(2))} m`
        : metersPerUnit >= 0.01
          ? `${parseFloat((metersPerUnit * 100).toFixed(2))} cm`
          : `${parseFloat((metersPerUnit * 1000).toFixed(2))} mm`;
    const res = resolutionRef.current;
    const Z = 0.1;
    const Y = -2;

    // Ruler line: (0, -2) → (1, -2) — always 1 world unit wide
    t.rulerGroup.add(
      makeLine2(
        [
          [0, Y],
          [1, Y],
        ],
        Z,
        0xffffff,
        2,
        res,
      ),
    );

    // End caps (short vertical ticks)
    t.rulerGroup.add(
      makeLine2(
        [
          [0, Y - 0.3],
          [0, Y + 0.3],
        ],
        Z,
        0xffffff,
        2,
        res,
      ),
    );
    t.rulerGroup.add(
      makeLine2(
        [
          [1, Y - 0.3],
          [1, Y + 0.3],
        ],
        Z,
        0xffffff,
        2,
        res,
      ),
    );

    // Label
    const labelDiv = document.createElement('div');
    labelDiv.style.cssText =
      'color:#fff;font-size:11px;font-weight:600;font-family:monospace;white-space:nowrap;pointer-events:none;text-shadow:0 0 4px #000;';
    labelDiv.textContent = `1 unit = ${label}`;
    const labelObj = new CSS2DObject(labelDiv);
    labelObj.position.set(0.5, Y - 0.8, Z);
    t.rulerGroup.add(labelObj);
  }, [metersPerUnit]);

  // ══════════════════════════════════════════════════════════════════════════
  //  MEASUREMENT RENDERING
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;
    clearGroup(t.measureGroup);

    if (measurementPoints.length === 0) return;

    const mpu = metersPerUnit;
    const res = resolutionRef.current;
    const Z = 0.6;

    // Draw points and lines
    for (let i = 0; i < measurementPoints.length; i++) {
      const [x, y] = measurementPoints[i];

      // Point marker (unit circle, scaled in animation loop)
      const dot = new THREE.Mesh(
        new THREE.CircleGeometry(1, 16),
        new THREE.MeshBasicMaterial({ color: 0xd4ff33, depthWrite: false, depthTest: false, transparent: true }),
      );
      dot.position.set(x, y, Z);
      dot.userData.dotScreenPx = DOT_SCREEN_PX;
      t.measureGroup.add(dot);

      // Line segment + label
      if (i > 0) {
        const [px, py] = measurementPoints[i - 1];
        t.measureGroup.add(
          makeLine2(
            [
              [px, py],
              [x, y],
            ],
            Z,
            0xd4ff33,
            3,
            res,
          ),
        );

        // Distance label
        const dist = Math.hypot(x - px, y - py) * mpu;
        const labelDiv = document.createElement('div');
        labelDiv.style.cssText =
          'background:#d4ff33;color:#000;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;font-family:monospace;white-space:nowrap;pointer-events:none;';
        labelDiv.textContent = dist < 1 ? `${(dist * 100).toFixed(1)} cm` : `${dist.toFixed(2)} m`;
        const label = new CSS2DObject(labelDiv);
        label.position.set((px + x) / 2, (py + y) / 2, Z);
        t.measureGroup.add(label);
      }
    }

    // Total distance label (if more than 2 points)
    if (measurementPoints.length > 2) {
      let total = 0;
      for (let i = 1; i < measurementPoints.length; i++) {
        const [px, py] = measurementPoints[i - 1];
        const [x, y] = measurementPoints[i];
        total += Math.hypot(x - px, y - py) * mpu;
      }
      const last = measurementPoints[measurementPoints.length - 1];
      const totalDiv = document.createElement('div');
      totalDiv.style.cssText =
        'background:#d4ff33;color:#000;padding:3px 8px;border-radius:4px;font-size:12px;font-weight:700;font-family:monospace;white-space:nowrap;pointer-events:none;';
      totalDiv.textContent = `\u03A3 ${total < 1 ? `${(total * 100).toFixed(1)} cm` : `${total.toFixed(2)} m`}`;
      const totalLabel = new CSS2DObject(totalDiv);
      totalLabel.position.set(last[0], last[1] + 1, Z);
      t.measureGroup.add(totalLabel);
    }
  }, [measurementPoints, metersPerUnit]);

  // ══════════════════════════════════════════════════════════════════════════
  //  SCALE CALIBRATION POINTS RENDERING
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;
    clearGroup(t.scaleGroup);

    if (scalePoints.length === 0) return;

    const res = resolutionRef.current;
    const Z = 0.8;
    const CROSS_COLOR = 0xff0000;

    // Draw red crosshair markers at each point
    for (const [x, y] of scalePoints) {
      // Horizontal bar of crosshair
      const hBar = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 0.15),
        new THREE.MeshBasicMaterial({ color: CROSS_COLOR, depthWrite: false, depthTest: false, transparent: true }),
      );
      hBar.position.set(x, y, Z);
      hBar.userData.dotScreenPx = 12;
      t.scaleGroup.add(hBar);

      // Vertical bar of crosshair
      const vBar = new THREE.Mesh(
        new THREE.PlaneGeometry(0.15, 1),
        new THREE.MeshBasicMaterial({ color: CROSS_COLOR, depthWrite: false, depthTest: false, transparent: true }),
      );
      vBar.position.set(x, y, Z);
      vBar.userData.dotScreenPx = 12;
      t.scaleGroup.add(vBar);
    }

    // Draw red line between the two points
    if (scalePoints.length === 2) {
      const [p1, p2] = scalePoints;
      t.scaleGroup.add(
        makeLine2(
          [
            [p1[0], p1[1]],
            [p2[0], p2[1]],
          ],
          Z,
          CROSS_COLOR,
          3,
          res,
        ),
      );

      // Distance label (in coordinate units — scale is being calibrated)
      const pixDist = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
      const distDiv = document.createElement('div');
      distDiv.style.cssText =
        'background:rgba(255,0,0,0.85);color:#fff;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;font-family:monospace;white-space:nowrap;pointer-events:none;';
      distDiv.textContent = `${pixDist.toFixed(1)} units`;
      const distLabel = new CSS2DObject(distDiv);
      distLabel.position.set((p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2, Z);
      t.scaleGroup.add(distLabel);
    }
  }, [scalePoints]);

  // ══════════════════════════════════════════════════════════════════════════
  //  BOUNDING BOX FOR SELECTED POLYGON
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const t = threeRef.current;
    if (!t) return;
    clearGroup(t.bboxGroup);

    if (activePolygonIndex === null || !polygons[activePolygonIndex]) return;

    const poly = polygons[activePolygonIndex];
    const pts = poly.points;
    if (pts.length < 2) return;

    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    for (const [x, y] of pts) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }

    const Z = 0.5;
    const mpu = metersPerUnit;
    const res = resolutionRef.current;

    // Blue dashed bounding rectangle
    const bboxPts: [number, number][] = [
      [minX, minY],
      [maxX, minY],
      [maxX, maxY],
      [minX, maxY],
      [minX, minY],
    ];
    t.bboxGroup.add(makeDashedLine2(bboxPts, Z, 0x00cfff, 1.5, 0.9, res, 0.8, 0.5));

    // Width label (bottom center)
    const width = (maxX - minX) * mpu;
    const widthDiv = document.createElement('div');
    widthDiv.style.cssText =
      'background:rgba(0,0,0,0.75);color:#fff;padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500;font-family:monospace;white-space:nowrap;pointer-events:none;';
    widthDiv.textContent = width < 1 ? `${(width * 100).toFixed(1)} cm` : `${width.toFixed(2)} m`;
    const widthLabel = new CSS2DObject(widthDiv);
    widthLabel.position.set((minX + maxX) / 2, minY - 0.5, Z);
    t.bboxGroup.add(widthLabel);

    // Height label (right center)
    const height = (maxY - minY) * mpu;
    const heightDiv = document.createElement('div');
    heightDiv.style.cssText =
      'background:rgba(0,0,0,0.75);color:#fff;padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500;font-family:monospace;white-space:nowrap;pointer-events:none;';
    heightDiv.textContent = height < 1 ? `${(height * 100).toFixed(1)} cm` : `${height.toFixed(2)} m`;
    const heightLabel = new CSS2DObject(heightDiv);
    heightLabel.position.set(maxX + 0.5, (minY + maxY) / 2, Z);
    t.bboxGroup.add(heightLabel);
  }, [polygons, activePolygonIndex, metersPerUnit]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        cursor:
          viewMode === '3d' ? 'grab' : tool === 'measure' ? 'crosshair' : tool === 'select' ? 'default' : 'crosshair',
        touchAction: 'none',
      }}
    />
  );
}
